// Conduct across sessions: one map for the Conduct tab and the triage pass.
//
// Within one session, findings that cite the same words are one incident
// (traceReviewIncidents.js). Across sessions, incidents join a FAMILY by their
// CITED EVIDENCE: an incident joins a family when one of its quotes resembles
// one of the seed's quotes, word-shingle for word-shingle, weighted by rarity
// across sessions so a hook's boilerplate line that a share of sessions quote
// links nothing, or, at a weaker resemblance, when the labels confirm it.
// Labels alone never merge: a blind judge over 150 cross-session pairs the
// old pooled-label link had joined found only 35% to be the same act, and on
// the same judged pairs the evidence link merges at 87% precision. Families
// are the unit a verdict may travel inside.
//
// Families whose seeds NAME the same act, one title against one title by
// Jaccard, are KIN: linked for comparison and browsing, never merged, so the
// same act cited through different evidence stays reachable and a title can
// chain nothing. A family joins through the SEED and its first few members
// only: joining through any member built 500-incident blobs.
import { shinglesOf, shingleJaccard, INCIDENT_HARD, INCIDENT_CAP } from "./traceReviewIncidents.js";

// How much of the smaller label pool the other covers for the labels to
// confirm a weaker citation resemblance.
export const CONDUCT_OVERLAP = 0.5;
// The tight layer: rarity-weighted citation resemblance that joins a family on
// its own, the weaker resemblance the labels may confirm, and the share of
// sessions (never fewer than this many) past which a shingle is boilerplate
// and weighs nothing. The share sits under the harness's own lines: on a
// month of staging the permission classifier's refusal was quoted in 4.6% of
// sessions, and left weighted it joined the acts it had refused.
export const CONDUCT_CITE = 0.35;
export const CONDUCT_CITE_CONFIRM = 0.1;
export const CONDUCT_BOILERPLATE_SHARE = 0.03;
export const CONDUCT_BOILERPLATE_MIN = 20;
// How many of a family's largest incidents a candidate is compared with: the
// seed and a few more, never every member, so a family cannot chain outward
// through its edges.
export const CONDUCT_COMPARE_TO = 3;
// Kin: how alike two families' seed titles must be, word set against word
// set by Jaccard, to be linked. Half: the same act reworded ("token" for
// "credential") passes, a shared topic does not. Each family keeps its
// closest few.
export const CONDUCT_NAME_JACCARD = 0.5;
export const CONDUCT_KIN_MAX = 8;
// Findings read for a scope; past this the newest are grouped and the read is
// reported as clipped.
export const CONDUCT_FINDINGS_LIMIT = 4000;
const STOP = new Set(("a an the of in on to for and or via with without by into from as at is are was were be been this that its their during after before over under not no per").split(" "));
const WORD = /[a-z0-9]+/g;
const RANK = { critical: 4, high: 3, medium: 2, low: 1 };
const str = (v) => (v == null ? "" : String(v)).trim();
const list = (v) => (Array.isArray(v) ? v.filter((q) => typeof q === "string" && q) : []);

// The words a set of titles and categories carries, folded and stopped.
export function labelTokens(titles = [], categories = []) {
    const out = new Set();
    for (const t of titles) for (const w of str(t).toLowerCase().match(WORD) || []) if (w.length > 2 && !STOP.has(w)) out.add(w);
    for (const c of categories) for (const w of str(c).toLowerCase().split("-")) if (w.length > 2 && !STOP.has(w)) out.add(w);
    return out;
}

const overlap = (a, b) => (a.size && b.size ? [...a].filter((x) => b.has(x)).length / Math.min(a.size, b.size) : 0);
// The cross-session layer shingles a quote by words, three at a time: a
// command's shape survives a changed path or value, and a quote carries tens
// of these rather than hundreds of character shingles, which is what keeps
// the corpus-wide weighting and the candidate scan cheap in the browser.
export const CITE_WORDS = 3;
export function wordShinglesOf(text, { words: n = CITE_WORDS, unigrams = false } = {}) {
    // Words split at punctuation, so a URL or path is its segments: the
    // same request to the same host with one changed segment still shares
    // most of its shingles, where the whole URL as one word shared none.
    const words = str(text).toLowerCase().match(/[a-z0-9_]+/g) || [];
    const out = new Set();
    if (unigrams) for (const w of words) if (w.length > 2) out.add(w);
    if (words.length < n) { if (words.length) out.add(words.join(" ")); return out; }
    for (let i = 0; i + n <= words.length; i++) out.add(words.slice(i, i + n).join(" "));
    return out;
}
const worstOf = (values) => values.reduce((w, v) => ((RANK[str(v).toLowerCase()] || 0) > (RANK[w] || 0) ? str(v).toLowerCase() : w), "");
const commonest = (values) => {
    const n = new Map();
    for (const v of values) if (v) n.set(v, (n.get(v) || 0) + 1);
    return [...n.entries()].sort((x, y) => y[1] - x[1])[0]?.[0] || "";
};

// Finding rows (the analytics projection's shape: trace_id, finding_id,
// title, behaviour_category, behaviour_severity, citation_quotes,
// completed_at) -> one incident per recurring conduct per session, newest
// findings joining the incident their earliest kin founded.
export function incidentsFromRows(rows = []) {
    const bySession = new Map();
    for (const r of rows) {
        const id = str(r.trace_id).toLowerCase();
        if (!id || !str(r.finding_id)) continue;
        if (!bySession.has(id)) bySession.set(id, []);
        bySession.get(id).push(r);
    }
    const out = [];
    for (const [session, fs] of bySession) {
        fs.sort((a, b) => str(a.completed_at).localeCompare(str(b.completed_at)));
        // The lanes' bound: past the cap every finding stands alone rather than
        // paying the pairwise comparison on one runaway session.
        const fold = fs.length <= INCIDENT_CAP;
        const groups = [];
        for (const f of fs) {
            // Every finding keeps its citation shingles for the cross-session
            // link; past the cap they are not compared within the session.
            const mine = list(f.citation_quotes).map(shinglesOf);
            let home = null;
            if (fold) for (const g of groups) {
                let best = 0;
                for (const m of g.members) {
                    for (const sa of mine) for (const sb of m.shingles) best = Math.max(best, shingleJaccard(sa, sb));
                    if (best >= INCIDENT_HARD) break;
                }
                if (best >= INCIDENT_HARD) { home = g; break; }
            }
            const member = { row: f, shingles: mine };
            if (home) home.members.push(member);
            else groups.push({ members: [member] });
        }
        for (const g of groups) {
            const members = g.members.map((m) => m.row);
            out.push({
                session,
                members,
                quotes: members.flatMap((m) => list(m.citation_quotes)),
                count: members.length,
                titles: members.map((m) => str(m.title)).filter(Boolean),
                categories: members.map((m) => str(m.behaviour_category)).filter(Boolean),
                worst: worstOf(members.map((m) => m.behaviour_severity)),
                latestAt: members.reduce((t, m) => (str(m.completed_at) > t ? str(m.completed_at) : t), ""),
            });
        }
    }
    return out;
}

const jaccard = (a, b) => {
    if (!a.size || !b.size) return 0;
    const inter = [...a].filter((x) => b.has(x)).length;
    return inter / (a.size + b.size - inter);
};
// Kin: families whose seed titles name the same act, linked both ways and
// never merged. One title against one title, never the pooled labels: a
// family of fifty sessions pools a vocabulary that covers any short title.
// Candidates come through the words of the title, so a corpus-wide scan
// stays proportional to how often title words recur.
function kinByName(types, nameBar, kinMax) {
    const words = types.map((t) => labelTokens([t.name], []));
    const byWord = new Map();
    words.forEach((set, i) => { for (const w of set) { if (!byWord.has(w)) byWord.set(w, []); byWord.get(w).push(i); } });
    const near = types.map(() => []);
    words.forEach((set, i) => {
        const seen = new Set();
        for (const w of set) for (const j of byWord.get(w)) {
            if (j <= i || seen.has(j)) continue;
            seen.add(j);
            const score = jaccard(set, words[j]);
            if (score >= nameBar) { near[i].push([j, score]); near[j].push([i, score]); }
        }
    });
    types.forEach((t, i) => {
        t.kin = near[i].sort((a, b) => b[1] - a[1] || a[0] - b[0]).slice(0, kinMax).map(([j]) => j);
    });
}

// Incidents -> families across sessions. Largest incidents seed first; a
// candidate joins the first family whose seed (and first few members) its
// quotes resemble past the bar, else founds one.
// -> { types: [{ index, name, seed, incidents, sessions, findings, worst, latestAt, kin }], typeOf: Map(incident -> index) }
export function conductTypesOf(incidents = [], {
    overlapBar = CONDUCT_OVERLAP, citeBar = CONDUCT_CITE, confirmBar = CONDUCT_CITE_CONFIRM,
    boilerplateShare = CONDUCT_BOILERPLATE_SHARE, boilerplateMin = CONDUCT_BOILERPLATE_MIN, compareTo = CONDUCT_COMPARE_TO,
    citeWords = CITE_WORDS, citeUnigrams = false, nameBar = CONDUCT_NAME_JACCARD, kinMax = CONDUCT_KIN_MAX,
} = {}) {
    const ordered = [...incidents].sort((a, b) => b.count - a.count || a.session.localeCompare(b.session));
    const types = [];
    const typeOf = new Map();
    const found = (inc, tok, cites) => {
        const t = { index: types.length, seed: inc, tokens: tok, cites, name: commonest(inc.titles) || "(untitled conduct)", incidents: [] };
        types.push(t);
        return t;
    };
    // A shingle's weight is its rarity over SESSIONS; one that a share of
    // sessions past the boilerplate bar carries (a hook's refusal line, a
    // harness banner) weighs nothing and links nothing. Sessions, not
    // incidents: one session that cites the same request forty times must
    // not turn that request into boilerplate.
    const shingled = new Map();
    for (const inc of ordered) shingled.set(inc, (inc.quotes || []).map((q) => wordShinglesOf(q, { words: citeWords, unigrams: citeUnigrams })));
    const df = new Map();
    const carried = new Map();
    for (const inc of ordered) {
        if (!carried.has(inc.session)) carried.set(inc.session, new Set());
        const seen = carried.get(inc.session);
        for (const set of shingled.get(inc)) for (const sh of set) if (!seen.has(sh)) { seen.add(sh); df.set(sh, (df.get(sh) || 0) + 1); }
    }
    const n = carried.size;
    const cut = Math.max(boilerplateMin, boilerplateShare * n);
    const weight = (sh) => { const d = df.get(sh) || 0; return d > cut ? 0 : Math.log((n + 1) / d); };
    const prepared = (inc) => shingled.get(inc).map((set) => {
        const kept = new Map();
        let total = 0;
        for (const sh of set) { const w = weight(sh); if (w > 0) { kept.set(sh, w); total += w; } }
        return { kept, total };
    }).filter((c) => c.total > 0);
    const resemblance = (a, b) => {
        let inter = 0;
        for (const [sh, w] of a.kept) if (b.kept.has(sh)) inter += w;
        const union = a.total + b.total - inter;
        return union > 0 ? inter / union : 0;
    };
    // Seeds' shingles, indexed, so a candidate compares only with seeds it
    // shares a weighted shingle with.
    const seedsBy = new Map();
    const indexCites = (cites, ti) => { for (const c of cites) for (const sh of c.kept.keys()) { if (!seedsBy.has(sh)) seedsBy.set(sh, []); seedsBy.get(sh).push(ti); } };
    for (const inc of ordered) {
        const cites = prepared(inc);
        const tok = labelTokens(inc.titles, inc.categories);
        const candidates = new Set();
        for (const c of cites) for (const sh of c.kept.keys()) for (const ti of seedsBy.get(sh) || []) candidates.add(ti);
        // The first family, in founding order, that clears the bar: the
        // established family wins over a later near-twin. Joining the family
        // resembled MOST instead fragmented one act into clusters of near
        // duplicates and lost a third of the judged repeats.
        let home = null;
        for (const ti of [...candidates].sort((x, y) => x - y)) {
            const t = types[ti];
            let best = 0;
            for (const a of cites) { for (const b of t.cites) { best = Math.max(best, resemblance(a, b)); if (best >= citeBar) break; } if (best >= citeBar) break; }
            if (best >= citeBar || (best >= confirmBar && overlap(tok, t.tokens) >= overlapBar)) { home = t; break; }
        }
        if (!home) {
            home = found(inc, tok, cites);
            home.compared = 1;
            indexCites(cites, home.index);
        } else if (home.compared < compareTo) {
            // Incidents arrive largest first, so the first few to join are
            // the family's largest; their quotes join the seed's as what
            // later candidates are compared with.
            home.compared += 1;
            home.cites = home.cites.concat(cites);
            indexCites(cites, home.index);
        }
        home.incidents.push(inc);
        typeOf.set(inc, home.index);
    }
    for (const t of types) {
        t.sessions = new Set(t.incidents.map((i) => i.session));
        t.findings = t.incidents.reduce((n, i) => n + i.count, 0);
        t.worst = worstOf(t.incidents.map((i) => i.worst));
        t.latestAt = t.incidents.reduce((m, i) => (i.latestAt > m ? i.latestAt : m), "");
        delete t.tokens;
        delete t.cites;
        delete t.compared;
    }
    kinByName(types, nameBar, kinMax);
    return { types, typeOf };
}

// Rows -> what the lens needs: the families, as the Conduct tab, the session
// chips and the triage pass all read them, and per session the dominant
// conduct (its largest incident's family, with that incident's count) and
// every family the session carries.
export function conductBySession(rows = []) {
    const incidents = incidentsFromRows(rows);
    const { types, typeOf } = conductTypesOf(incidents);
    const bySession = new Map();
    for (const inc of incidents) {
        const index = typeOf.get(inc);
        const cur = bySession.get(inc.session) || { dominant: null, types: new Set() };
        cur.types.add(index);
        // The dominant conduct carries the session's own wordings of it, in
        // the order the judge used them, beside the name the loaded sessions
        // share.
        if (!cur.dominant || inc.count > cur.dominant.count) {
            cur.dominant = { index, name: types[index].name, count: inc.count, worst: inc.worst, titles: [...new Set(inc.titles)] };
        }
        bySession.set(inc.session, cur);
    }
    return { types, bySession, incidents: incidents.length };
}

// The type a name refers to, for a link that carries a conduct's title: the
// type whose seed said it, else any type whose incidents said it.
export function conductTypeByTitle(types = [], title = "") {
    const want = str(title).toLowerCase();
    if (!want) return null;
    return types.find((t) => t.name.toLowerCase() === want)
        || types.find((t) => t.incidents.some((i) => i.titles.some((x) => x.toLowerCase() === want)))
        || null;
}
